import React, { useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { IPropsOf } from 'types';
import * as Yup from 'yup';
import { useContext } from 'react';
import { WLContext } from '@contexts';
import { translations } from 'translations';
import { Trans, useTranslation } from 'react-i18next';
import { FileField } from '@design-system/inputs';
import { Modal } from '@design-system/elements';
import fp from 'lodash/fp';
import { isValidFileFormat, isValidFileSize } from '@helpers';
import { css, cx } from '@emotion/css';
import { InfoIcon } from '@chakra-ui/icons';

export interface ICsvFormProps extends IPropsOf<'form'> {
  isOpen: boolean;
  onSubmit: (v: { file: File | null }) => Promise<void>;
  onToggle: (v?: boolean) => void;
}

export const CsvForm: React.FC<ICsvFormProps> = (props): JSX.Element => {
  const { isOpen, onSubmit, onToggle, ...rest } = props;

  const { color } = useContext(WLContext);
  const { t } = useTranslation();

  // Define schema inside component to avoid initialization timing issues
  const schema = React.useMemo(
    () =>
      Yup.object().shape({
        file: Yup.mixed()
          .test('file-size', translations.ERRORS.MAX_FILE_SIZE, (v) => {
            if (fp.isNil(v)) return true;
            if (fp.isString(v)) return true;
            return isValidFileSize(v.size);
          })
          .test('file-format', translations.ERRORS.ONLY_CSV_FILES, (v) => {
            if (fp.isNil(v)) return true;
            if (fp.isString(v)) return true;
            return isValidFileFormat(v.type, ['text/csv']);
          })
          .required(translations.ERRORS.REQUIRED),
      }),
    [],
  );

  const { control, formState, handleSubmit, reset } = useForm<{
    file: File | null;
  }>({
    defaultValues: { file: null },
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const handleOnSubmit = useCallback(
    async (v: { file: File | null }) => {
      await onSubmit(v);
      reset();
    },
    [onSubmit, reset],
  );

  return (
    <form id="csv-form" onSubmit={handleSubmit(handleOnSubmit)} {...rest}>
      <Modal
        closeOnSuccess={false}
        formId="csv-form"
        handleCancel={reset}
        isDisabled={!formState.isValid || formState.isSubmitting}
        isLoading={formState.isSubmitting}
        showModal={isOpen}
        setShowModal={onToggle}
        title={t(translations.CONTACTS.CSV_TITLE)}
        width="md:w-6/12 lg:w-4/12">
        <FileField
          accept=".csv"
          className="mb-6 w-full"
          control={control}
          label=""
          labelFontSize="md"
          name="file"
          primaryColor={color}
        />
        <div className="mt-4 flex">
          <InfoIcon color="#C4C4C4" height={4} width={4} />
          <p className="ml-2 text-xs font-medium">
            <Trans i18nKey={translations.CONTACTS.CSV_HELPER}>
              <span className="text-gray-300" />
              <a
                className={cx(
                  'font-bold',
                  css`
                    color: ${color};
                  `,
                )}
                download
                href="/templates/csv-template.csv"
              />
            </Trans>
          </p>
        </div>
      </Modal>
    </form>
  );
};
